import { Alert, Snackbar } from "@mui/material";
import CryptoJS from "crypto-js";
import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { OptionsData } from "../types";
import { fetchOAuthToken, setLogoutHandler, setRefreshTokenHandler } from "../utilities/api";
import { useOptions } from "./OptionsContext";

// Global context cleanup handlers registry
const contextCleanupHandlers: (() => void)[] = [];

export const registerContextCleanupHandler = (handler: () => void) => {
  contextCleanupHandlers.push(handler);
};

export const unregisterContextCleanupHandler = (handler: () => void) => {
  const index = contextCleanupHandlers.indexOf(handler);
  if (index > -1) {
    contextCleanupHandlers.splice(index, 1);
  }
};

const clearAllContexts = () => {
  console.log("[UserContext] Clearing all registered contexts:", contextCleanupHandlers.length);
  contextCleanupHandlers.forEach((handler, index) => {
    try {
      handler();
    } catch (error) {
      console.error(`[UserContext] Error clearing context ${index}:`, error);
    }
  });
};

/**
 * Clears CloudFront authentication cookies that might interfere with new user logins
 */
const clearCloudFrontCookies = () => {
  // Clear CloudFront cookies AND Django session cookies
  const cookies = [
    "CloudFront-Policy",
    "CloudFront-Signature",
    "CloudFront-Key-Pair-Id",
    "sessionid", // Django session cookie
    "csrftoken", // Django CSRF token
    "django_session" // Alternative Django session name
  ];
  const expiry = "expires=Thu, 01 Jan 1970 00:00:00 UTC";

  console.log("[UserContext] 🍪 Clearing cookies:", cookies);

  // Clear for current domain
  cookies.forEach((cookie) => {
    document.cookie = `${cookie}=; ${expiry}; path=/;`;
    console.log(`[UserContext] 🍪 Cleared cookie: ${cookie} for current domain`);
  });

  // Clear for parent domain (e.g., .culturemap.app)
  const hostname = window.location.hostname;
  if (hostname.includes(".")) {
    const domain = hostname.split(".").slice(-2).join(".");
    cookies.forEach((cookie) => {
      document.cookie = `${cookie}=; ${expiry}; path=/; domain=.${domain};`;
      console.log(`[UserContext] 🍪 Cleared cookie: ${cookie} for domain: .${domain}`);
    });
  }
};

/**
 * Interface for the OAuth token response
 */
interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope: string;
}

/**
 * Defines the structure of the user profile information.
 */
interface Profile {
  email: string;
  name?: string;
  picture?: string;
}

/**
 * Props for the UserProvider component.
 */
interface UserProviderProps {
  children: ReactNode;
}

/**
 * Defines the context type for user-related operations.
 */
export interface UserInfo extends Profile {
  organisation: {
    id: number;
    name: string;
    description: string;
  };
  fulcrum_api_key?: string;
  has_fulcrum_api_key?: boolean;
  fulcrum_region?: "AU" | "US" | "EU";
  is_staff?: boolean;
  is_superuser?: boolean;
}

interface UserContextType {
  profile: UserInfo | null;
  setProfile: React.Dispatch<React.SetStateAction<UserInfo | null>>;
  login: (username: string, password: string) => Promise<void>;
  logout: (expired?: boolean) => void;
  basicAuthHeader?: { Authorization: string };
  oAuthHeader?: { Authorization: string };
  isInitialized: boolean;
  getStoredToken: () => string | null;
  setStoredToken: (token: string) => void;
  clearStoredData: () => void;
  hasWritePermissions: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * Custom hook to use the user context.
 * @returns The user context with profile and auth functions.
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

/**
 * Builds the basic auth header using environment variables.
 * @returns An object containing the Authorization header or undefined if credentials are missing.
 */
const buildBasicAuthHeader = () => {
  const username = process.env.REACT_APP_BASIC_AUTH_USER;
  const password = process.env.REACT_APP_BASIC_AUTH_PASSWORD;
  return username && password ? { Authorization: `Basic ${btoa(`${username}:${password}`)}` } : undefined;
};

// Add interface for the useOptions hook return type
interface OptionsContextValue {
  options: OptionsData | null;
  refreshOptions: () => Promise<void>;
  isLoading: boolean;
}

export class UserProfileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UserProfileError";
  }
}

export class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthenticationError";
  }
}

/**
 * Provides user authentication context.
 * @deprecated use `authStore` & `useAuth` hooks instead
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [profile, setProfile] = useState<UserInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const basicAuthHeader = buildBasicAuthHeader();
  const { refreshOptions } = useOptions() as OptionsContextValue;
  const [isInitialized, setIsInitialized] = useState(false);

  const login = React.useCallback(
    async (username: string, password: string) => {
      try {
        console.log("[UserContext] 🔐 LOGIN: Starting login process for user:", username);

        // Clear ALL authentication state immediately to prevent conflicts
        console.log("[UserContext] 🧹 LOGIN: Clearing all session storage");
        localStorage.clear();
        setProfile(null);
        setAccessToken(null);
        setRefreshToken(null);

        // Clear CloudFront cookies to prevent authentication conflicts
        // These cookies might belong to a previously logged-in user
        console.log("[UserContext] 🍪 LOGIN: Clearing CloudFront cookies");
        clearCloudFrontCookies();

        const formData = new FormData();
        formData.append("username", username);
        formData.append("password", password);
        formData.append("scope", "read");
        formData.append("grant_type", "password");

        const baseUrl = process.env.REACT_APP_API_ROOT;

        const response = await fetch(`${baseUrl}o/token/`, {
          method: "POST",
          body: formData,
          credentials: "include"
        });

        if (!response.ok) {
          throw new AuthenticationError("Failed to authenticate");
        }

        const tokenData: TokenResponse = await response.json();
        const userInfoResponse = await fetch(`${baseUrl}api/user/me/`, {
          headers: {
            Authorization: `Bearer ${tokenData.access_token}`,
            "Content-Type": "application/json"
          },
          cache: "no-store"
        });

        if (!userInfoResponse.ok) {
          throw new UserProfileError("Failed to fetch user info");
        }

        const userInfo: UserInfo = await userInfoResponse.json();
        console.log("[UserContext] 👤 LOGIN: User info received:", {
          email: userInfo.email,
          name: userInfo.name,
          is_staff: userInfo.is_staff,
          is_superuser: userInfo.is_superuser,
          organisation: userInfo.organisation?.name
        });

        setProfile(userInfo);
        setAccessToken(tokenData.access_token);
        setRefreshToken(tokenData.refresh_token || null);

        // Store encrypted data in session storage
        const encryptedProfile = CryptoJS.AES.encrypt(JSON.stringify(userInfo), process.env.REACT_APP_ENCRYPTION_KEY || "").toString();
        const encryptedAccessToken = CryptoJS.AES.encrypt(tokenData.access_token, process.env.REACT_APP_ENCRYPTION_KEY || "").toString();

        localStorage.setItem("profile", encryptedProfile);
        localStorage.setItem("accessToken", encryptedAccessToken);

        if (tokenData.refresh_token) {
          const encryptedRefreshToken = CryptoJS.AES.encrypt(
            tokenData.refresh_token,
            process.env.REACT_APP_ENCRYPTION_KEY || ""
          ).toString();
          localStorage.setItem("refreshToken", encryptedRefreshToken);
        }

        // Explicitly refresh options after successful login
        await refreshOptions();

        setIsInitialized(true);

        console.log("[UserContext] ✅ LOGIN: Login completed successfully for:", userInfo.email);
      } catch (error) {
        console.error("[UserContext] ❌ LOGIN: Login failed:", error);

        // CRITICAL: Ensure we don't revert to old user data on login failure
        // Keep everything cleared even if login fails
        console.log("[UserContext] 🧹 LOGIN-ERROR: Ensuring all data remains cleared after login failure");
        setProfile(null);
        setAccessToken(null);
        setRefreshToken(null);
        localStorage.clear();
        clearCloudFrontCookies();

        throw error;
      }
    },
    [refreshOptions]
  );

  useEffect(() => {
    const loadStoredProfile = async () => {
      try {
        console.log("[UserContext] 🔄 INIT: Loading stored profile from session");
        const storedProfile = localStorage.getItem("profile");
        const storedAccessToken = localStorage.getItem("accessToken");

        console.log("[UserContext] 🔄 INIT: Found stored data:", {
          hasProfile: !!storedProfile,
          hasToken: !!storedAccessToken
        });

        if (storedProfile && storedAccessToken) {
          const bytesProfile = CryptoJS.AES.decrypt(storedProfile, process.env.REACT_APP_ENCRYPTION_KEY || "");
          const decryptedProfile = bytesProfile.toString(CryptoJS.enc.Utf8);
          const bytesAccessToken = CryptoJS.AES.decrypt(storedAccessToken, process.env.REACT_APP_ENCRYPTION_KEY || "");
          const decryptedAccessToken = bytesAccessToken.toString(CryptoJS.enc.Utf8);

          // Decrypt refresh token if it exists
          let decryptedRefreshToken = null;
          const storedRefreshToken = localStorage.getItem("refreshToken");
          if (storedRefreshToken) {
            const bytesRefreshToken = CryptoJS.AES.decrypt(storedRefreshToken, process.env.REACT_APP_ENCRYPTION_KEY || "");
            decryptedRefreshToken = bytesRefreshToken.toString(CryptoJS.enc.Utf8);
          }

          const parsedProfile = JSON.parse(decryptedProfile);
          console.log("[UserContext] 🔄 INIT: Restored profile from session:", {
            email: parsedProfile.email,
            is_staff: parsedProfile.is_staff,
            is_superuser: parsedProfile.is_superuser
          });

          setProfile(parsedProfile);
          setAccessToken(decryptedAccessToken);
          setRefreshToken(decryptedRefreshToken);
        } else {
          console.log("[UserContext] 🔄 INIT: No stored profile found");
        }

        // Always set initialized to true, even if no stored profile
        setIsInitialized(true);
      } catch (error) {
        console.error("[UserContext] ❌ INIT: Failed to load stored profile:", error);
        console.log("[UserContext] 🧹 INIT: Clearing corrupted session data");
        localStorage.clear();
        setIsInitialized(true);
      }
    };

    loadStoredProfile();
  }, []);

  /**
   * Logs out the current user.
   * @param expired Optional parameter to indicate if logout was due to token expiration
   */
  const logout = React.useCallback(
    async (expired: boolean = false) => {
      console.log("[UserContext] 🚪 LOGOUT: Starting comprehensive logout cleanup");
      console.log("[UserContext] 🚪 LOGOUT: Current profile before logout:", {
        email: profile?.email,
        is_staff: profile?.is_staff,
        is_superuser: profile?.is_superuser
      });

      // 0. Call backend logout endpoint FIRST to invalidate server session
      if (accessToken && !expired) {
        try {
          console.log("[UserContext] 🌐 LOGOUT: Calling backend logout endpoint");
          const baseUrl = process.env.REACT_APP_API_ROOT;
          const logoutResponse = await fetch(`${baseUrl}api/auth/logout/`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json"
            },
            credentials: "include"
          });

          if (logoutResponse.ok) {
            console.log("[UserContext] ✅ LOGOUT: Backend logout successful");
          } else {
            console.warn("[UserContext] ⚠️ LOGOUT: Backend logout failed:", logoutResponse.status);
          }
        } catch (error) {
          console.warn("[UserContext] ⚠️ LOGOUT: Error calling backend logout:", error);
        }
      } else {
        console.log("[UserContext] ⏭️ LOGOUT: Skipping backend logout (expired session or no token)");
      }

      // 1. Clear ALL registered contexts using the global cleanup system
      console.log("[UserContext] 🧹 LOGOUT: Clearing all registered contexts");
      clearAllContexts();

      // 2. Clear all localStorage comprehensively
      // This includes: profile, accessToken, refreshToken, admin settings, signed URLs, etc.
      console.log("[UserContext] 🧹 LOGOUT: Clearing localStorage");
      const sessionKeys = Object.keys(localStorage);
      console.log("[UserContext] 🧹 LOGOUT: localStorage keys before clear:", sessionKeys);
      localStorage.clear();

      // Also clear any localStorage if used (future-proofing)
      try {
        // Clear specific localStorage items that might exist
        console.log("[UserContext] 🧹 LOGOUT: Clearing localStorage items");
        localStorage.removeItem("tokenData"); // From Fulcrum integration
      } catch (error) {
        console.warn("[UserContext] ⚠️ LOGOUT: Error clearing localStorage:", error);
      }

      // 3. Clear CloudFront cookies
      console.log("[UserContext] 🍪 LOGOUT: Clearing CloudFront cookies");
      clearCloudFrontCookies();

      // 4. Clear all auth state IMMEDIATELY to prevent further API calls
      // This will trigger React effects that depend on profile/tokens
      console.log("[UserContext] 🔄 LOGOUT: Clearing React state");
      setProfile(null);
      setAccessToken(null);
      setRefreshToken(null);

      // 5. Refresh options (this should probably wait for next login)
      console.log("[UserContext] 🔄 LOGOUT: Refreshing options");
      refreshOptions();

      // 6. Show expired message if needed
      if (expired) {
        console.log("[UserContext] ⏰ LOGOUT: Setting expired message");
        setError("Your session has expired. Please log in again.");
      }

      console.log("[UserContext] ✅ LOGOUT: Logout cleanup completed");
    },
    [refreshOptions, profile, accessToken]
  );

  const oAuthHeader = React.useMemo(() => (accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined), [accessToken]);

  // Handle closing the error snackbar
  const handleCloseError = () => {
    setError(null);
  };

  // Centralized localStorage access
  const getStoredToken = () => localStorage.getItem("accessToken");
  const setStoredToken = (token: string) => localStorage.setItem("accessToken", token);
  const clearStoredData = () => localStorage.clear();

  // Helper function to check if user has write permissions
  const hasWritePermissions = (): boolean => {
    if (!profile) {
      return false;
    }

    return profile.is_staff === true || profile.is_superuser === true;
  };

  // Expose these through context
  const value = {
    profile,
    setProfile,
    basicAuthHeader,
    oAuthHeader,
    isInitialized,
    login,
    logout,
    getStoredToken,
    setStoredToken,
    clearStoredData,
    hasWritePermissions
  };

  useEffect(() => {
    setLogoutHandler(logout);
    return () => setLogoutHandler(null);
  }, [logout]);

  // Add refresh token function with minimal logging
  const refreshAccessToken = React.useCallback(async (): Promise<boolean> => {
    if (!refreshToken) {
      return false;
    }

    try {
      const formData = new FormData();
      formData.append("grant_type", "refresh_token");
      formData.append("refresh_token", refreshToken);

      const tokenData: TokenResponse = await fetchOAuthToken(formData);

      setAccessToken(tokenData.access_token);
      if (tokenData.refresh_token) {
        setRefreshToken(tokenData.refresh_token);
      }

      // Update session storage
      const encryptedAccessToken = CryptoJS.AES.encrypt(tokenData.access_token, process.env.REACT_APP_ENCRYPTION_KEY || "").toString();
      localStorage.setItem("accessToken", encryptedAccessToken);

      if (tokenData.refresh_token) {
        const encryptedRefreshToken = CryptoJS.AES.encrypt(tokenData.refresh_token, process.env.REACT_APP_ENCRYPTION_KEY || "").toString();
        localStorage.setItem("refreshToken", encryptedRefreshToken);
      }

      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    }
  }, [refreshToken]);

  // Set refresh token handler with minimal logging
  useEffect(() => {
    setRefreshTokenHandler(refreshAccessToken);
    return () => setRefreshTokenHandler(null);
  }, [refreshAccessToken]);

  return (
    <UserContext.Provider value={value}>
      {children}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleCloseError} severity='error' variant='filled' sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </UserContext.Provider>
  );
};
